// AI Optimizer — multi-armed bandit stream reweighting
// (audit Phase 3: AI Optimizer).
//
// Two backend endpoints in ``server.py`` drive the UI:
//   GET  /api/campaigns/{cid}/ai-optimize/preview  → propose new weights
//   POST /api/campaigns/{cid}/ai-optimize/apply    → persist them
//
// The page lives at /campaigns/:campaignId/ai-optimize. Operator picks
// an algorithm (Thompson / UCB1 / ε-greedy), sees a proposal table
// alongside the current weights, and clicks "Apply" to write the new
// weights back. Previewing is read-only; apply is audit-logged on the
// backend so a curious operator can answer "why did stream X
// suddenly take 60% of the traffic?" later.

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { useT } from '../i18n';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Brain, ArrowLeftRight, Save, RefreshCw, ArrowLeft, Undo2 } from 'lucide-react';
import ConfirmDialog from './ConfirmDialog';

const ALGORITHMS = [
  { value: 'thompson', label: 'Thompson sampling (default)' },
  { value: 'ucb1', label: 'UCB1 (deterministic)' },
  { value: 'epsilon_greedy', label: 'ε-greedy (simple)' },
];

const DEFAULT_OPTIONS = {
  algorithm: 'thompson',
  lookback_days: 30,
  min_clicks_per_stream: 0,
  floor: 5,
  epsilon: 0.1,
  prior_alpha: 1.0,
  prior_beta: 1.0,
  seed: '',
  samples: 4000,
};

export default function AIOptimizer() {
  const { campaignId } = useParams();
  const { t } = useT();

  const [campaign, setCampaign] = useState(null);
  const [options, setOptions] = useState(DEFAULT_OPTIONS);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  // Seed + options of the last preview, so Apply writes exactly the weights shown.
  const [previewRun, setPreviewRun] = useState(null);
  const [confirmApply, setConfirmApply] = useState(false);
  // Weights before the last Apply, for one-click undo.
  const [undoWeights, setUndoWeights] = useState(null);
  const [undoing, setUndoing] = useState(false);

  const loadCampaign = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/campaigns/${campaignId}`);
      setCampaign(res.data);
    } catch (e) {
      console.error(e);
      toast.error(t('Failed to load campaign'));
    }
  }, [campaignId, t]);

  const runPreview = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        algorithm: options.algorithm,
        lookback_days: Number(options.lookback_days) || 30,
        min_clicks_per_stream: Number(options.min_clicks_per_stream) || 0,
        floor: Number(options.floor) || 5,
        epsilon: Number(options.epsilon) || 0.1,
        prior_alpha: Number(options.prior_alpha) || 1.0,
        prior_beta: Number(options.prior_beta) || 1.0,
        samples: Number(options.samples) || 4000,
      };
      // Thompson sampling is random: fix a seed so Apply repeats this exact result.
      const hasSeed = options.seed !== '' && options.seed !== null && options.seed !== undefined;
      params.seed = hasSeed ? Number(options.seed) : Math.floor(Math.random() * 2147483647);
      const res = await axios.get(
        `${API}/campaigns/${campaignId}/ai-optimize/preview`,
        { params }
      );
      setPreview(res.data);
      setPreviewRun({ seed: params.seed, key: JSON.stringify(options) });
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || t('Failed to preview optimization'));
    } finally {
      setLoading(false);
    }
  }, [campaignId, options, t]);

  const apply = async () => {
    setApplying(true);
    try {
      const body = {
        algorithm: options.algorithm,
        lookback_days: Number(options.lookback_days) || 30,
        min_clicks_per_stream: Number(options.min_clicks_per_stream) || 0,
        floor: Number(options.floor) || 5,
        epsilon: Number(options.epsilon) || 0.1,
        prior_alpha: Number(options.prior_alpha) || 1.0,
        prior_beta: Number(options.prior_beta) || 1.0,
        samples: Number(options.samples) || 4000,
      };
      if (previewRun) body.seed = previewRun.seed;
      const before = (preview?.proposals || [])
        .filter((p) => p.proposed_weight !== p.current_weight)
        .map((p) => ({ stream_id: p.stream_id, weight: p.current_weight }));
      const res = await axios.post(
        `${API}/campaigns/${campaignId}/ai-optimize/apply`,
        body
      );
      setPreview(res.data);
      setUndoWeights(before.length ? before : null);
      toast.success(
        t('Applied {n} weight changes', { n: res.data?.applied ?? 0 }) ||
          `Applied ${res.data?.applied ?? 0} weight changes`
      );
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || t('Failed to apply optimization'));
    } finally {
      setApplying(false);
    }
  };

  const undo = async () => {
    if (!undoWeights) return;
    setUndoing(true);
    try {
      await Promise.all(
        undoWeights.map((w) => axios.put(`${API}/streams/${w.stream_id}`, { weight: w.weight })),
      );
      setUndoWeights(null);
      toast.success(t('Previous weights are back'));
      runPreview();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('Could not restore the previous weights'));
    } finally {
      setUndoing(false);
    }
  };

  useEffect(() => {
    loadCampaign();
  }, [loadCampaign]);

  const stale = !previewRun || previewRun.key !== JSON.stringify(options);

  const proposals = preview?.proposals || [];
  const skipped = preview?.skipped || [];
  const summary = preview?.summary || {
    stream_count: 0,
    total_abs_drift: 0,
    max_drift: 0,
  };

  return (
    <div className="container mx-auto p-6 max-w-6xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
            <Link to="/campaigns" className="inline-flex items-center gap-1 hover:underline">
              <ArrowLeft className="h-4 w-4" /> {t('Campaigns') || 'Campaigns'}
            </Link>
            {campaign && (
              <>
                <span>/</span>
                <Link
                  to={`/campaigns/${campaignId}/streams`}
                  className="hover:underline"
                >
                  {campaign.name}
                </Link>
              </>
            )}
          </div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Brain className="h-7 w-7 text-primary" />
            {t('AI Optimizer') || 'AI Optimizer'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {t(
              'Rebalance stream weights based on historical conversion rate using a multi-armed bandit.'
            ) ||
              'Rebalance stream weights based on historical conversion rate using a multi-armed bandit.'}
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('Settings') || 'Settings'}</CardTitle>
          <CardDescription>
            {t(
              'Tune the bandit before running a preview. Apply only writes weights to active streams.'
            ) ||
              'Tune the bandit before running a preview. Apply only writes weights to active streams.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <div className="space-y-2">
            <Label>{t('Algorithm') || 'Algorithm'}</Label>
            <Select
              value={options.algorithm}
              onValueChange={(v) => setOptions((o) => ({ ...o, algorithm: v }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ALGORITHMS.map((a) => (
                  <SelectItem key={a.value} value={a.value}>
                    {a.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{t('Lookback (days)') || 'Lookback (days)'}</Label>
            <Input
              type="number"
              min="1"
              max="365"
              value={options.lookback_days}
              onChange={(e) =>
                setOptions((o) => ({ ...o, lookback_days: e.target.value }))
              }
            />
          </div>

          <div className="space-y-2">
            <Label>{t('Min clicks per stream') || 'Min clicks per stream'}</Label>
            <Input
              type="number"
              min="0"
              value={options.min_clicks_per_stream}
              onChange={(e) =>
                setOptions((o) => ({ ...o, min_clicks_per_stream: e.target.value }))
              }
            />
          </div>

          <div className="space-y-2">
            <Label>{t('Weight floor (%)') || 'Weight floor (%)'}</Label>
            <Input
              type="number"
              min="0"
              max="50"
              value={options.floor}
              onChange={(e) => setOptions((o) => ({ ...o, floor: e.target.value }))}
            />
          </div>

          {options.algorithm === 'epsilon_greedy' && (
            <div className="space-y-2">
              <Label>{t('Epsilon (ε)') || 'Epsilon (ε)'}</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max="1"
                value={options.epsilon}
                onChange={(e) =>
                  setOptions((o) => ({ ...o, epsilon: e.target.value }))
                }
              />
            </div>
          )}

          {options.algorithm === 'thompson' && (
            <div className="space-y-2">
              <Label>{t('Random seed (optional)') || 'Random seed (optional)'}</Label>
              <Input
                type="number"
                placeholder="e.g. 42"
                value={options.seed}
                onChange={(e) => setOptions((o) => ({ ...o, seed: e.target.value }))}
              />
            </div>
          )}
        </CardContent>
        <CardContent className="pt-0 flex flex-wrap items-center gap-3">
          <Button onClick={runPreview} disabled={loading} variant="outline">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {t('Preview') || 'Preview'}
          </Button>
          <Button onClick={() => setConfirmApply(true)} disabled={applying || !preview || stale} data-testid="ai-apply">
            <Save className="h-4 w-4 mr-2" />
            {t('Apply') || 'Apply'}
          </Button>
          {undoWeights && (
            <Button variant="outline" onClick={undo} disabled={undoing} data-testid="ai-undo">
              <Undo2 className="h-4 w-4 mr-2" />
              {t('Undo: return the previous weights')}
            </Button>
          )}
          {preview && stale && (
            <span className="text-sm text-muted-foreground">{t('Settings changed — run the preview again before applying.')}</span>
          )}
          {preview && (
            <span className="text-sm text-muted-foreground">
              {t('Proposed drift') || 'Proposed drift'}: {summary.total_abs_drift}{' '}
              {t('weight units') || 'weight units'} ·{' '}
              {t('max change') || 'max change'} {summary.max_drift}
            </span>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ArrowLeftRight className="h-5 w-5" />
            {t('Proposed weights') || 'Proposed weights'}
          </CardTitle>
          <CardDescription>
            {preview
              ? t(
                  'Compare current to proposed weights. Apply persists the proposed column to the streams.'
                ) ||
                'Compare current to proposed weights. Apply persists the proposed column to the streams.'
              : t('Run a preview to see proposed weights.') ||
                'Run a preview to see proposed weights.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {proposals.length === 0 && (
            <p className="text-sm text-muted-foreground">
              {preview
                ? t('No active streams to optimize.') || 'No active streams to optimize.'
                : t('No data yet.') || 'No data yet.'}
            </p>
          )}
          {proposals.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b">
                    <th className="py-2 pr-3">{t('Stream') || 'Stream'}</th>
                    <th className="py-2 pr-3">{t('Clicks') || 'Clicks'}</th>
                    <th className="py-2 pr-3">{t('Conversions') || 'Conversions'}</th>
                    <th className="py-2 pr-3">{t('Smoothed CR') || 'Smoothed CR'}</th>
                    <th className="py-2 pr-3">{t('Current') || 'Current'}</th>
                    <th className="py-2 pr-3">{t('Proposed') || 'Proposed'}</th>
                    <th className="py-2 pr-3">{t('Δ') || 'Δ'}</th>
                  </tr>
                </thead>
                <tbody>
                  {proposals.map((p) => {
                    const delta = p.proposed_weight - p.current_weight;
                    const deltaColor =
                      delta > 0 ? 'text-emerald-600' : delta < 0 ? 'text-rose-600' : 'text-muted-foreground';
                    return (
                      <tr key={p.stream_id} className="border-b last:border-b-0">
                        <td className="py-2 pr-3 font-medium">{p.name}</td>
                        <td className="py-2 pr-3">{p.clicks}</td>
                        <td className="py-2 pr-3">{p.conversions}</td>
                        <td className="py-2 pr-3">
                          {(p.smoothed_cr * 100).toFixed(2)}%
                        </td>
                        <td className="py-2 pr-3">{p.current_weight}</td>
                        <td className="py-2 pr-3 font-semibold">{p.proposed_weight}</td>
                        <td className={`py-2 pr-3 ${deltaColor}`}>
                          {delta > 0 ? '+' : ''}
                          {delta}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {skipped.length > 0 && (
            <div className="mt-4">
              <h3 className="text-sm font-medium mb-2">
                {t('Skipped (below min_clicks_per_stream)') ||
                  'Skipped (below min_clicks_per_stream)'}
              </h3>
              <div className="flex flex-wrap gap-2">
                {skipped.map((s) => (
                  <Badge key={s.stream_id} variant="outline">
                    {s.name}: {s.clicks} {t('clicks') || 'clicks'} · w={s.current_weight}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirmApply}
        onOpenChange={setConfirmApply}
        title={t('Change stream weights now?')}
        description={t('The new weights apply to running streams from the next click. You can undo this on this page.')}
        confirmLabel={t('Apply')}
        variant="default"
        loading={applying}
        onConfirm={() => { setConfirmApply(false); apply(); }}
      />
    </div>
  );
}
